// services/affiliateGapTable.js
import React, { useState } from 'react';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Hash,
  MousePointer,
  Flag,
  Globe,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Mail,
  Plus,
  Minus,
  Square,
  CheckSquare,
  Bookmark,
  BookmarkCheck,
  Eye,
  Target
} from 'lucide-react';

// Helper Components
export const ComparisonIndicator = ({ value, showArrow = true, className = "" }) => {
  if (!value || value === null || value === undefined) return null;

  const stringValue = value.toString();
  const cleanValue = stringValue.replace(/\*/g, '');

  const isPositive = cleanValue.startsWith('+');
  const isNegative = cleanValue.startsWith('-');

  if (!isPositive && !isNegative) return null;

  return (
    <div
      className={`flex justify-center items-center gap-1 text-xs font-medium ${
        isPositive ? 'text-green-600' : 'text-red-600'
      } ${className}`}
    >
      {showArrow &&
        (isPositive ? (
          <TrendingUp className="w-3 h-3 self-center" />
        ) : (
          <TrendingDown className="w-3 h-3 self-center" />
        ))}
      <span className="font-bold leading-none self-center">{cleanValue}</span>
    </div>
  );
};

export const SortIcon = ({ column, sortConfig }) => {
  if (sortConfig.key !== column) {
    return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
  }
  return sortConfig.direction === 'asc' ? 
    <ArrowUp className="w-4 h-4 text-blue-600" /> : 
    <ArrowDown className="w-4 h-4 text-blue-600" />;
};

export const SimpleContentBox = ({ title, icon, data, renderContent }) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="p-4">
        <h4 className="text-sm font-medium text-gray-800 mb-3 flex items-center gap-2">
          {icon && <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-1 rounded">{icon}</div>}
          {title}
        </h4>
        {renderContent ? renderContent() : (
          <div className="text-xs text-gray-600">No data available</div>
        )}
      </div>
    </div>
  );
};

// Plan limit service functions
export const getPlanDataLimit = (userPlan, tableLength) => {
  switch(userPlan) {
    case 'free': return 0;
    case 'growth': return 25;
    case 'pro': return 500;
    case 'enterprise': return null; // unlimited
    default: return 0;
  }
};

// Fetch user plan
export const fetchUserPlan = async () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
  
  try {
    const response = await fetch(`${API_BASE_URL}/api/planfetch`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Convert to lowercase to match plan configuration
    const planType = (data.plan || 'free').toLowerCase();
    
    return planType;
  } catch (error) {
    console.error('Error fetching user plan:', error);
    return 'free'; // Default to free plan on error
  }
};

// Get plan limit info message
export const getPlanLimitInfo = (userPlan, totalResults, planLimit) => {
  const planNames = {
    free: 'Free',
    growth: 'Growth',
    pro: 'Pro',
    enterprise: 'Enterprise'
  };
  
  const planName = planNames[userPlan] || 'Free';
  
  if (planLimit === null) {
    return `${totalResults} affiliates found (${planName} Plan: Full access to all data)`;
  } else if (planLimit === 0) {
    return `${totalResults} affiliates found (${planName} Plan: Preview only, upgrade to see data)`;
  } else {
    const dataRows = Math.min(planLimit, totalResults);
    const imageRows = Math.max(0, totalResults - planLimit);
    return `${totalResults} affiliates found (${planName} Plan: First ${dataRows} with full data${imageRows > 0 ? `, remaining ${imageRows} preview only` : ''})`;
  }
};

// Data fetching functions
export const fetchGapAnalysis = async (brandName, spyUrl) => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
  
  try {
    
    // Fetch user plan first
    const userPlan = await fetchUserPlan();
    
    const response = await fetch(`${API_BASE_URL}/api/gap/analyze`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        brandName: brandName,
        spyUrl: spyUrl
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('Gap analysis failed:', errorData);
      
      // Check if it's a limit exceeded error
      if (response.status === 403 || 
          errorData.error?.toLowerCase().includes('limit') || 
          errorData.message?.toLowerCase().includes('limit')) {
        
        // Extract limit info from error response
        const limitInfo = errorData.limitInfo || {};
        
        return {
          success: false,
          error: errorData.message || errorData.error || 'Gap analysis limit exceeded',
          isLimitExceeded: true,
          limitInfo: {
            currentAnalysisCount: limitInfo.currentAnalysisCount || 0,
            allowedAnalyses: limitInfo.allowedAnalyses || 0,
            remainingAnalyses: limitInfo.remainingAnalyses || 0,
            userPlan: limitInfo.userPlan || userPlan || 'free'
          }
        };
      }
      
      throw new Error(errorData.message || errorData.error || `Gap analysis failed - HTTP ${response.status}`);
    }

    const gapData = await response.json();

    // Check if this is a new brand that's still processing
    if (gapData.metadata?.processing === true) {
      return {
        success: true,
        processing: true,
        brandId: gapData.competitorBrand?.brandId,
        brandUrl: gapData.competitorBrand?.brandUrl,
        brandName: gapData.competitorBrand?.brandName,
        affiliates: [],
        totalCount: 0,
        rawData: gapData,
        userPlan: userPlan,
        isLimitExceeded: false,
        message: gapData.message || 'Gap brand added successfully. Processing started.'
      };
    }

    // Transform data with user plan context
    const affiliates = gapData.analysisData ?
      transformBackendData(gapData.analysisData, userPlan) :
      [];

    // Calculate plan limits for the response
    const tableLength = gapData.analysisData?.length || affiliates.length;
    const planLimit = getPlanDataLimit(userPlan, tableLength);

    return {
      success: true,
      processing: false,
      affiliates: affiliates,
      totalCount: tableLength,
      rawData: gapData,
      userPlan: userPlan,
      planLimit: planLimit,
      planLimitInfo: getPlanLimitInfo(userPlan, tableLength, planLimit),
      isLimitExceeded: false
    };

  } catch (error) {
    console.error('❌ Gap analysis error:', error.message);
    
    return {
      success: false,
      error: error.message,
      affiliates: [],
      isLimitExceeded: false,
      details: {
        brandName,
        spyUrl,
        timestamp: new Date().toISOString()
      }
    };
  }
};

export const fetchBrandData = async () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
  
  try {
    const response = await fetch(`${API_BASE_URL}/api/gap`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `Failed to fetch brands - HTTP ${response.status}`);
    }

    const result = await response.json();
    
    if (result.success && result.data) {
      return result.data;
    } else {
      throw new Error('Invalid response format from API');
    }
    
  } catch (error) {
    console.error('Error fetching brand data:', error.message);
    throw error;
  }
};

// Data transformation functions - WITH PLAN LIMITS
export const transformBackendData = (analysisDataArray, userPlan) => {
  if (!analysisDataArray || !Array.isArray(analysisDataArray)) {
    console.error('Invalid analysisDataArray:', analysisDataArray);
    return [];
  }


  // Get the plan limit based on the array length
  const planLimit = getPlanDataLimit(userPlan, analysisDataArray.length);

  return analysisDataArray.map((item, index) => {
    // Determine if this row should be shown as limited/blurred
    const isLimited = planLimit !== null && index >= planLimit;
    
    const currentTraffic = parseInt(item.total_traffic) || 0;
    const oldTraffic = parseInt(item.total_traffic_old) || null;
    const currentOrgKeywords = parseInt(item.organic_keywords) || 0;
    const oldOrgKeywords = parseInt(item.organic_keywords_old) || null;
    const currentOrgTraffic = parseInt(item.organic_traffic) || 0;
    const oldOrgTraffic = parseInt(item.organic_traffic_old) || null;
    const currentPaidTraffic = parseInt(item.paid_traffic) || 0;
    const oldPaidTraffic = parseInt(item.paid_traffic_old) || null;

    const totalTrafficComparison = calculateNumericDifference(currentTraffic, oldTraffic);
    const organicKeywordsComparison = calculateNumericDifference(currentOrgKeywords, oldOrgKeywords);
    const organicTrafficComparison = calculateNumericDifference(currentOrgTraffic, oldOrgTraffic);
    const paidTrafficComparison = calculateNumericDifference(currentPaidTraffic, oldPaidTraffic);
    
    const hasHistoricalData = oldTraffic !== null || 
                             oldOrgKeywords !== null || 
                             oldOrgTraffic !== null || 
                             oldPaidTraffic !== null;

    if (hasHistoricalData) {
    }

    const transformedItem = {
      id: `affiliate_${index}`,
      url: item.affiliate_url,
      totalTraffic: currentTraffic,
      organicKeywords: currentOrgKeywords,
      organicTraffic: currentOrgTraffic,
      paidTraffic: currentPaidTraffic,
      email: item.email || '',
      vertical: item.vertical || '',
      
      totalTrafficOld: oldTraffic,
      organicKeywordsOld: oldOrgKeywords,
      organicTrafficOld: oldOrgTraffic,
      paidTrafficOld: oldPaidTraffic,
      
      totalTrafficComparison,
      organicKeywordsComparison,
      organicTrafficComparison,
      paidTrafficComparison,
      
      geoBreakdown: parseGeoData(item.top_geos),
      geoBreakdownOld: parseGeoData(item.top_geos_old),
      
      hasHistoricalData,
      isLimited, // Flag to indicate if this row is limited by plan
      
      topPages: [
        `${item.affiliate_url}/`,
        `${item.affiliate_url}/games`,
        `${item.affiliate_url}/sports`,
        `${item.affiliate_url}/casino`,
        `${item.affiliate_url}/promotions`
      ],
      topKeywords: [
        { keyword: 'online casino', volume: '12K', position: 3 },
        { keyword: 'sports betting', volume: '8K', position: 5 },
        { keyword: 'live games', volume: '5K', position: 2 },
        { keyword: 'casino bonus', volume: '3K', position: 7 }
      ],
      topPaidKeywords: [
        { keyword: 'casino games', cpc: '$2.50', volume: '15K' },
        { keyword: 'bet online', cpc: '$3.20', volume: '10K' },
        { keyword: 'live casino', cpc: '$1.80', volume: '8K' }
      ]
    };

    return transformedItem;
  });
};

// Utility functions
export const calculateNumericDifference = (current, old) => {
  if (!old || old === 0 || old === null) return null;
  const currentVal = parseInt(current) || 0;
  const oldVal = parseInt(old) || 0;
  
  const difference = currentVal - oldVal;
  if (difference === 0) return null;
  
  return difference > 0 ? `+${difference}` : `${difference}`;
};

export const parseGeoData = (geoString) => {
  if (!geoString) return [];
  
  try {
    const geoEntries = geoString.split(', ');
    return geoEntries.map((entry, index) => {
      const lastColonIndex = entry.lastIndexOf(': ');
      const countryPart = entry.substring(0, lastColonIndex);
      const traffic = entry.substring(lastColonIndex + 2);
      const country = countryPart.trim();
      const trafficValue = parseInt(traffic) || 0;
      
      const countryCode = getCountryCode(country);
      
      return {
        country: country,
        code: countryCode,
        organic: trafficValue.toLocaleString(),
        paid: '0',
        total: trafficValue.toLocaleString()
      };
    }).slice(0, 5);
  } catch (error) {
    console.error('Error parsing geo data:', error);
    return [];
  }
};

export const formatNumber = (num) => {
  if (typeof num === 'string' && num.includes(',')) {
    return num;
  }
  
  const number = typeof num === 'string' ? parseInt(num.replace(/,/g, '')) : num;
  
  if (number >= 1000000) {
    return `${(number / 1000000).toFixed(1)}M`;
  } else if (number >= 1000) {
    return `${(number / 1000).toFixed(1)}K`;
  }
  return number.toString();
};

export const getCountryFlag = (countryCode) => {
  return `https://flagsapi.com/${countryCode}/flat/24.png`;
};

export const getCountryCode = (countryName) => {
  const countryMap = {
    'United Kingdom of Great Britain and Northern Ireland': 'GB',
    'United Kingdom': 'GB',
    'United States': 'US',
    'United States of America': 'US',
    'Germany': 'DE',
    'Canada': 'CA',
    'Nigeria': 'NG',
    'Pakistan': 'PK',
    'Spain': 'ES',
    'India': 'IN',
    'Indonesia': 'ID',
    'Singapore': 'SG',
    'Zimbabwe': 'ZW',
    'Cameroon': 'CM',
    'Ireland': 'IE',
    'Sri Lanka': 'LK',
    'France': 'FR',
    'Italy': 'IT',
    'Australia': 'AU',
    'Brazil': 'BR',
    'Congo (Democratic Republic of the)': 'CD',
    'Colombia': 'CO',
    'Mexico': 'MX',
    'Argentina': 'AR',
    'Netherlands': 'NL',
    'Belgium': 'BE',
    'Switzerland': 'CH',
    'Austria': 'AT',
    'Sweden': 'SE',
    'Norway': 'NO',
    'Denmark': 'DK',
    'Finland': 'FI',
    'Poland': 'PL',
    'Portugal': 'PT',
    'Greece': 'GR',
    'Turkey': 'TR',
    'Russia': 'RU',
    'Ukraine': 'UA',
    'South Africa': 'ZA',
    'Egypt': 'EG',
    'Kenya': 'KE',
    'Ghana': 'GH',
    'Morocco': 'MA',
    'Tunisia': 'TN',
    'Algeria': 'DZ',
    'Ethiopia': 'ET',
    'Uganda': 'UG',
    'Tanzania': 'TZ',
    'China': 'CN',
    'Japan': 'JP',
    'South Korea': 'KR',
    'Thailand': 'TH',
    'Malaysia': 'MY',
    'Philippines': 'PH',
    'Vietnam': 'VN',
    'Bangladesh': 'BD',
    'Nepal': 'NP',
    'Israel': 'IL',
    'Saudi Arabia': 'SA',
    'United Arab Emirates': 'AE',
    'Qatar': 'QA',
    'Kuwait': 'KW',
    'Jordan': 'JO',
    'Lebanon': 'LB',
    'Chile': 'CL',
    'Peru': 'PE',
    'Ecuador': 'EC',
    'Venezuela': 'VE',
    'Uruguay': 'UY',
    'Paraguay': 'PY',
    'Bolivia': 'BO',
    'Costa Rica': 'CR',
    'Panama': 'PA',
    'Guatemala': 'GT',
    'Honduras': 'HN',
    'El Salvador': 'SV',
    'Nicaragua': 'NI',
    'Dominican Republic': 'DO',
    'Cuba': 'CU',
    'Jamaica': 'JM',
    'Haiti': 'HT',
    'New Zealand': 'NZ',
    'Czech Republic': 'CZ',
    'Czechia': 'CZ',
    'Hungary': 'HU',
    'Romania': 'RO',
    'Bulgaria': 'BG',
    'Slovakia': 'SK',
    'Slovenia': 'SI',
    'Croatia': 'HR',
    'Serbia': 'RS',
    'Bosnia and Herzegovina': 'BA',
    'Albania': 'AL',
    'North Macedonia': 'MK',
    'Montenegro': 'ME',
    'Kosovo': 'XK',
    'Estonia': 'EE',
    'Latvia': 'LV',
    'Lithuania': 'LT',
    'Belarus': 'BY',
    'Moldova': 'MD',
    'Georgia': 'GE',
    'Armenia': 'AM',
    'Azerbaijan': 'AZ',
    'Kazakhstan': 'KZ',
    'Uzbekistan': 'UZ',
    'Turkmenistan': 'TM',
    'Kyrgyzstan': 'KG',
    'Tajikistan': 'TJ',
    'Afghanistan': 'AF',
    'Iran': 'IR',
    'Iraq': 'IQ',
    'Syria': 'SY',
    'Yemen': 'YE',
    'Oman': 'OM',
    'Bahrain': 'BH',
    'Cyprus': 'CY',
    'Malta': 'MT',
    'Iceland': 'IS',
    'Luxembourg': 'LU',
    'Monaco': 'MC',
    'Liechtenstein': 'LI',
    'Andorra': 'AD',
    'San Marino': 'SM',
    'Vatican City': 'VA',
    'Mauritius': 'MU',
    'Seychelles': 'SC',
    'Madagascar': 'MG',
    'Mozambique': 'MZ',
    'Zambia': 'ZM',
    'Botswana': 'BW',
    'Namibia': 'NA',
    'Angola': 'AO',
    'Myanmar': 'MM',
    'Burma': 'MM',
    'Cambodia': 'KH',
    'Laos': 'LA',
    'Mongolia': 'MN',
    'Bhutan': 'BT',
    'Brunei': 'BN',
    'East Timor': 'TL',
    'Timor-Leste': 'TL',
    'Papua New Guinea': 'PG',
    'Fiji': 'FJ',
    'Solomon Islands': 'SB',
    'Vanuatu': 'VU',
    'Samoa': 'WS',
    'Tonga': 'TO',
    'Kiribati': 'KI',
    'Tuvalu': 'TV',
    'Nauru': 'NR',
    'Palau': 'PW',
    'Marshall Islands': 'MH',
    'Micronesia': 'FM',
    'Unknown': 'XX'
  };
  
  return countryMap[countryName] || 'XX';
};

// Table sorting and filtering
export const getSortedData = (affiliateData, sortConfig, searchTerm, showDraftOnly, draftAffiliates) => {
  let sortableData = [...affiliateData];
  
  if (showDraftOnly) {
    sortableData = sortableData.filter(item => draftAffiliates.has(item.id));
  }
  
  if (searchTerm) {
    sortableData = sortableData.filter(item =>
      item.url && item.url.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  if (sortConfig.key) {
    sortableData.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (['totalTraffic', 'organicKeywords', 'organicTraffic', 'paidTraffic'].includes(sortConfig.key)) {
        aValue = parseInt(aValue) || 0;
        bValue = parseInt(bValue) || 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  return sortableData;
};

// Export to CSV - RESPECTS PLAN LIMITS
export const exportToCSV = (data, userPlan) => {
  const planLimit = getPlanDataLimit(userPlan, data.length);
  
  const headers = [
    'URL',
    'Total Traffic',
    'Organic Keywords',
    'Organic Traffic',
    'Paid Traffic',
    'Email',
    'Top Countries',
    'Traffic Change'
  ];

  const rows = data.map((item, index) => {
    // Check if this row is limited by plan
    const isLimited = planLimit !== null && index >= planLimit;
    
    if (isLimited) {
      // Return limited data for rows beyond plan limit
      return [
        item.url,
        '[Upgrade to view]',
        '[Upgrade to view]',
        '[Upgrade to view]',
        '[Upgrade to view]',
        '[Upgrade to view]',
        '[Upgrade to view]',
        '[Upgrade to view]'
      ];
    }
    
    const topCountries = item.geoBreakdown?.slice(0, 3)
      .map(geo => `${geo.country} (${geo.total})`)
      .join('; ') || '';

    return [
      item.url,
      item.totalTraffic,
      item.organicKeywords,
      item.organicTraffic,
      item.paidTraffic,
      item.email || '',
      topCountries,
      item.totalTrafficComparison || 'N/A'
    ];
  });

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');

  return csvContent;
};

// Email templates for gap analysis
export const gapAnalysisEmailTemplates = [
  {
    name: "Gap Analysis Partnership Inquiry",
    subject: "Partnership Opportunity - Gap Analysis Insights",
    message: `Hello,

I hope this email finds you well. My name is [Your Name] from [Your Company].

Through our gap analysis research, we've identified your website as having strong performance in areas where we see potential for collaboration.

Our analysis shows complementary strengths between our organizations:
• Your strong performance in [specific markets/keywords]
• Our expertise in [your strengths]
• Mutual opportunities for audience expansion
• Potential for strategic partnerships

We believe there are several ways we could work together:
• Cross-promotional opportunities
• Content collaboration initiatives
• Affiliate partnership programs
• Joint marketing campaigns
• Resource sharing agreements

Would you be interested in exploring how our organizations might collaborate to leverage these identified opportunities?

I'd be happy to share more specific insights from our analysis and discuss potential partnership models.

Looking forward to hearing from you.

Best regards,
[Your Name]
[Your Title]
[Your Contact Information]`
  },
  {
    name: "Strategic Alliance Proposal",
    subject: "Strategic Alliance Opportunity - Market Gap Analysis",
    message: `Hi there,

I'm [Your Name] from [Your Company], and I've been conducting gap analysis in our industry.

Your website has emerged as a key player in areas where we see strategic alignment opportunities. Our analysis reveals:

• Market gaps where collaboration could benefit both parties
• Complementary audience segments
• Mutual growth opportunities
• Shared target demographics with different approaches

Potential collaboration areas:
• Joint content initiatives
• Cross-referral programs
• Shared marketing campaigns
• Resource pooling for better market coverage
• Strategic partnerships for market expansion

We believe that strategic collaboration could help both our organizations fill identified market gaps more effectively than competing alone.

Would you be open to a discussion about potential strategic alliance opportunities?

Best regards,
[Your Name]
[Your Position]
[Contact Details]`
  },
  {
    name: "Market Opportunity Discussion",
    subject: "Market Opportunity Discussion - Gap Analysis Results",
    message: `Hello,

I'm reaching out from [Your Company] following our comprehensive gap analysis of the [industry] market.

Our research has identified your organization as having strong positioning in key market segments, and we've discovered several opportunities where our organizations could work together to capture additional market share.

Key findings from our analysis:
• Untapped market segments where collaboration could be beneficial
• Complementary strengths that could be leveraged together
• Opportunities for mutual audience expansion
• Potential for increased market penetration through partnership

We're interested in exploring:
• Strategic partnerships
• Joint venture opportunities
• Collaborative marketing initiatives
• Resource sharing agreements
• Co-development projects

Would you be available for a brief discussion about how we might work together to capitalize on these identified market opportunities?

Thank you for your time and consideration.

Best regards,
[Your Name]
[Your Contact Information]`
  }
];

// Table component for the data grid - WITH CONDITIONAL SORTING BASED ON PLAN
export const AffiliateDataTable = ({
  currentData,
  visibleColumns,
  sortConfig,
  handleSort,
  expandedRows,
  selectedRows,
  draftAffiliates,
  toggleRowExpansion,
  handleRowSelection,
  toggleDraftAffiliate,
  openEmailPopup,
  formatNumber,
  ComparisonIndicator,
  SortIcon,
  getCountryFlag,
  handleGeoMouseEnter,
  handleGeoMouseLeave,
  SimpleContentBox,
  userPlan // User plan to control sorting
}) => {
  
  // Check if sorting should be disabled for this plan
  const isSortingDisabled = userPlan === 'growth' || userPlan === 'pro';

  // Helper function to render header content
  const renderHeaderContent = (icon, text, sortKey) => {
    if (isSortingDisabled) {
      // Render as non-clickable div for growth/pro plans
      return (
        <div className="flex items-center justify-center gap-2 text-xs font-medium text-blue-600 tracking-wider">
          {icon}
          {text}
        </div>
      );
    } else {
      // Render as clickable button for free/enterprise plans
      return (
        <button 
          onClick={() => handleSort(sortKey)}
          className="flex items-center justify-center gap-2 text-xs font-medium text-blue-600 tracking-wider hover:text-blue-700 transition-colors w-full"
        >
          {icon}
          {text}
          <SortIcon column={sortKey} sortConfig={sortConfig} />
        </button>
      );
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
          <tr>
            <th className="px-6 py-4 text-center whitespace-nowrap">
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => handleRowSelection('all')}
                  className={`transition-colors duration-200 ${
                    selectedRows.size === currentData.length && currentData.length > 0
                      ? 'text-blue-600 hover:text-blue-700'
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {selectedRows.size === currentData.length && currentData.length > 0 ? (
                    <CheckSquare className="w-4 h-4" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                </button>
              </div>
            </th>

            {visibleColumns.url && (
              <th className="px-6 py-4 text-center whitespace-nowrap">
                {renderHeaderContent(
                  <Globe className="w-4 h-4" />,
                  "Affiliate URL",
                  "url"
                )}
              </th>
            )}

            {visibleColumns.totalTraffic && (
              <th className="px-6 py-4 text-center whitespace-nowrap">
                {renderHeaderContent(
                  <BarChart3 className="w-4 h-4" />,
                  "Total Traffic",
                  "totalTraffic"
                )}
              </th>
            )}

            {visibleColumns.organicKeywords && (
              <th className="px-6 py-4 text-center whitespace-nowrap">
                {renderHeaderContent(
                  <Hash className="w-4 h-4" />,
                  "Organic Keywords",
                  "organicKeywords"
                )}
              </th>
            )}

            {visibleColumns.organicTraffic && (
              <th className="px-6 py-4 text-center whitespace-nowrap">
                {renderHeaderContent(
                  <TrendingUp className="w-4 h-4" />,
                  "Organic Traffic",
                  "organicTraffic"
                )}
              </th>
            )}

            {visibleColumns.paidTraffic && (
              <th className="px-6 py-4 text-center whitespace-nowrap">
                {renderHeaderContent(
                  <MousePointer className="w-4 h-4" />,
                  "Paid Traffic",
                  "paidTraffic"
                )}
              </th>
            )}

            {visibleColumns.topGeos && (
              <th className="px-6 py-4 text-center text-xs font-medium text-blue-600 tracking-wider whitespace-nowrap">
                <div className="flex items-center justify-center gap-2">
                  <Flag className="w-4 h-4" />
                  Top Geos
                </div>
              </th>
            )}

            {visibleColumns.contact && (
              <th className="px-6 py-4 text-center text-xs font-medium text-blue-600 tracking-wider whitespace-nowrap">
                <div className="flex items-center justify-center gap-2">
                  <Mail className="w-4 h-4" />
                  Contact
                </div>
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {currentData.map((affiliate, index) => (
            <React.Fragment key={affiliate.id}>
              <tr className={`hover:bg-gray-50 transition-all duration-200 ${
                expandedRows.has(affiliate.id) ? 'bg-blue-50' : ''
              } ${selectedRows.has(affiliate.id) ? 'bg-blue-50' : ''} ${
                affiliate.isLimited ? 'opacity-60 bg-gray-100' : ''
              }`}>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => handleRowSelection(affiliate.id)}
                      className={`transition-colors duration-200 ${
                        selectedRows.has(affiliate.id)
                          ? 'text-blue-600 hover:text-blue-700'
                          : 'text-gray-400 hover:text-gray-600'
                      }`}
                    >
                      {selectedRows.has(affiliate.id) ? (
                        <CheckSquare className="w-4 h-4" />
                      ) : (
                        <Square className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => toggleRowExpansion(affiliate.id)}
                      className={`text-blue-600 hover:text-blue-800 transition-all duration-200 transform hover:scale-110 ${
                        expandedRows.has(affiliate.id) ? 'rotate-180' : ''
                      }`}
                      disabled={affiliate.isLimited}
                    >
                      {expandedRows.has(affiliate.id) ? 
                        <Minus className="w-4 h-4" /> : 
                        <Plus className="w-4 h-4" />
                      }
                    </button>
                    <button
                      onClick={() => toggleDraftAffiliate(affiliate.id)}
                      className={`transition-colors ${
                        draftAffiliates.has(affiliate.id)
                          ? 'text-blue-600 hover:text-blue-800'
                          : 'text-gray-400 hover:text-gray-600'
                      }`}
                    >
                      {draftAffiliates.has(affiliate.id) ? 
                        <BookmarkCheck className="w-4 h-4" /> : 
                        <Bookmark className="w-4 h-4" />
                      }
                    </button>
                  </div>
                </td>
                {visibleColumns.url && (
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer transition-colors">
                      {affiliate.url}
                    </div>
                  </td>
                )}
                {visibleColumns.totalTraffic && (
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="space-y-1">
                      <div className="text-sm font-medium text-gray-900">
                        {affiliate.isLimited ? '[Upgrade to view]' : formatNumber(affiliate.totalTraffic)}
                      </div>
                      {!affiliate.isLimited && <ComparisonIndicator value={affiliate.totalTrafficComparison} />}
                    </div>
                  </td>
                )}
                {visibleColumns.organicKeywords && (
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="space-y-1">
                      <div className="text-sm text-gray-900">
                        {affiliate.isLimited ? '[Upgrade to view]' : formatNumber(affiliate.organicKeywords)}
                      </div>
                      {!affiliate.isLimited && <ComparisonIndicator value={affiliate.organicKeywordsComparison} />}
                    </div>
                  </td>
                )}
                {visibleColumns.organicTraffic && (
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="space-y-1">
                      <div className="text-sm font-medium text-blue-600">
                        {affiliate.isLimited ? '[Upgrade to view]' : formatNumber(affiliate.organicTraffic)}
                      </div>
                      {!affiliate.isLimited && <ComparisonIndicator value={affiliate.organicTrafficComparison} />}
                    </div>
                  </td>
                )}
                {visibleColumns.paidTraffic && (
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="space-y-1">
                      <div className="text-sm font-medium text-blue-600">
                        {affiliate.isLimited ? '[Upgrade to view]' : formatNumber(affiliate.paidTraffic)}
                      </div>
                      {!affiliate.isLimited && <ComparisonIndicator value={affiliate.paidTrafficComparison} />}
                    </div>
                  </td>
                )}
                {visibleColumns.topGeos && (
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {affiliate.isLimited ? (
                      <div className="text-sm text-gray-500">[Upgrade to view]</div>
                    ) : (
                      <div 
                        className="relative group cursor-pointer"
                        onMouseEnter={(e) => handleGeoMouseEnter(e, affiliate.geoBreakdown)}
                        onMouseLeave={handleGeoMouseLeave}
                      >
                        <div className="inline-flex items-center gap-1 text-xs">
                          {affiliate.geoBreakdown?.slice(0, 2).map((geo, index) => (
                            <span key={geo.code} className="inline-flex items-center gap-1">
                              {index > 0 && <span className="text-gray-400">, </span>}
                              <span className="text-gray-700">{geo.code}</span>
                              <span className="text-blue-600 font-medium">[{formatNumber(geo.organic)}]</span>
                            </span>
                          ))}
                          {(affiliate.geoBreakdown?.length || 0) > 2 && (
                            <span className="text-gray-500 ml-1 underline decoration-dotted">
                              , +{affiliate.geoBreakdown.length - 2} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </td>
                )}
                {visibleColumns.contact && (
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="space-y-1">
                      <button 
                        onClick={() => openEmailPopup(affiliate)}
                        className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-xs font-medium py-1 px-3 rounded-lg transition-all duration-300 flex items-center gap-1 transform hover:scale-105 hover:shadow-lg shadow-blue-600/30 mx-auto"
                        disabled={affiliate.isLimited}
                      >
                        <Mail className="w-3 h-3" />
                        Contact
                      </button>
                    </div>
                  </td>
                )}
              </tr>

              {/* Expanded Row Content - Only show if not limited */}
              {expandedRows.has(affiliate.id) && !affiliate.isLimited && (
                <tr className="animate-in slide-in-from-top-4 duration-300">
                  <td colSpan="8" className="px-6 py-6 bg-gradient-to-r from-blue-50 to-gray-50">
                    <div className="max-w-full overflow-hidden">
                      <div className="space-y-6">
                        
                        {/* Country Breakdown */}
                        {affiliate.geoBreakdown && affiliate.geoBreakdown.length > 0 && (
                          <div className="transform transition-all duration-500 hover:scale-[1.02]">
                            <h4 className="text-xs font-medium text-gray-800 mb-3 flex items-center gap-2">
                              <Flag className="w-4 h-4 text-blue-600" />
                              Country Traffic Breakdown
                            </h4>
                            <div className="w-full">
                              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                                <div className="overflow-x-auto">
                                  <table className="min-w-full">
                                    <thead className="bg-gray-100">
                                      <tr>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 tracking-wider whitespace-nowrap">Country</th>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 tracking-wider whitespace-nowrap">Organic Traffic</th>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 tracking-wider whitespace-nowrap">Paid Traffic</th>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 tracking-wider whitespace-nowrap">Total Traffic</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                      {affiliate.geoBreakdown.map((geo, index) => {
                                        const oldGeo = affiliate.geoBreakdownOld?.find(o => o.country === geo.country);
                                        const currentOrganic = parseInt(geo.organic.replace(/,/g, '')) || 0;
                                        const oldOrganic = oldGeo ? (parseInt(oldGeo.organic.replace(/,/g, '')) || 0) : 0;
                                        const organicDiff = currentOrganic - oldOrganic;
                                        const showDiff = affiliate.hasHistoricalData && oldOrganic > 0;

                                        return (
                                          <tr key={index} className="hover:bg-blue-50 transition-colors duration-200">
                                            <td className="px-4 py-3 text-xs font-medium text-gray-900 whitespace-nowrap text-center">
                                              <div className="flex items-center justify-center gap-2">
                                                <img 
                                                  src={getCountryFlag(geo.code)} 
                                                  alt={geo.country} 
                                                  className="w-6 h-4 object-cover rounded"
                                                  onError={(e) => {
                                                    e.target.src = getCountryFlag('XX');
                                                  }}
                                                />
                                                {geo.country}
                                              </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-center">
                                              <div className="space-y-1">
                                                <div className="text-xs text-gray-900 font-medium">{formatNumber(geo.organic)}</div>
                                                {showDiff && (
                                                  <div className={`text-xs font-medium ${
                                                    organicDiff > 0 ? 'text-green-600' : organicDiff < 0 ? 'text-red-600' : 'text-gray-500'
                                                  }`}>
                                                    {organicDiff > 0 ? '+' : ''}{organicDiff.toLocaleString()}
                                                  </div>
                                                )}
                                              </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-center">
                                              <div className="space-y-1">
                                                <div className="text-xs text-gray-900 font-medium">{formatNumber(geo.paid)}</div>
                                              </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-center">
                                              <div className="space-y-1">
                                                <div className="text-xs text-gray-900 font-medium">{formatNumber(geo.total)}</div>
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                          
                          {/* Top Pages */}
                          <SimpleContentBox
                            title="Top Pages"
                            icon={<Eye className="w-5 h-5 text-white" />}
                            data={affiliate.topPages}
                            renderContent={() => (
                              <div className="space-y-3">
                                {affiliate.topPages?.map((page, index) => (
                                  <div key={index} className="flex items-center gap-2 p-2 rounded-lg hover:bg-blue-50 transition-colors duration-200 cursor-pointer border border-transparent hover:border-blue-200">
                                    <span className="text-xs font-bold text-white bg-blue-600 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 shadow-lg">
                                      {index + 1}
                                    </span>
                                    <span className="text-xs text-gray-700 leading-tight font-medium">{page}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          />

                          {/* Top Organic Keywords */}
                          <SimpleContentBox
                            title="Top Organic Keywords"
                            icon={<Target className="w-5 h-5 text-white" />}
                            data={affiliate.topKeywords}
                            renderContent={() => (
                              <div className="space-y-3">
                                {affiliate.topKeywords?.map((keyword, index) => (
                                  <div key={index} className="flex items-center justify-between p-2 rounded-lg hover:bg-blue-50 transition-colors duration-200 border border-transparent hover:border-blue-200">
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                      <span className="text-xs font-bold text-white bg-blue-600 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 shadow-lg">
                                        {index + 1}
                                      </span>
                                      <span className="text-xs text-gray-700 break-words font-medium">{keyword.keyword}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          />

                          {/* Paid Keywords */}
                          <SimpleContentBox
                            title="Paid Keywords"
                            icon={<BarChart3 className="w-5 h-5 text-white" />}
                            data={affiliate.topPaidKeywords}
                            renderContent={() => (
                              <div className="space-y-3">
                                {affiliate.topPaidKeywords?.map((keyword, index) => (
                                  <div key={index} className="flex items-center justify-between p-2 rounded-lg hover:bg-blue-50 transition-colors duration-200 border border-transparent hover:border-blue-200">
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                      <span className="text-xs font-bold text-white bg-blue-600 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 shadow-lg">
                                        {index + 1}
                                      </span>
                                      <span className="text-xs text-gray-700 break-words font-medium">{keyword.keyword}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};